// Onboarding checklist — panel "Začínáme s laboratoří" pro nově vzniklou
// prázdnou laboratoř. Zobrazuje se jen lab-adminovi na Dashboardu.
//
// Kroky se odškrtávají automaticky z dat (žádný per-krok stav v DB):
//   - počty entit z /dashboard/stats (etalony, metodiky, přístroje)
//   - enabledQuantities + labInfoComplete z /lab-settings
//
// Panel zmizí, když: uživatel není admin, je onboardingDismissed, nebo jsou
// všechny kroky hotové. "Přeskočit vše" volá POST /lab-settings/onboarding/dismiss.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, ChevronRight, X } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useStats } from '@/contexts/StatsContext';

export default function OnboardingChecklist() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stats } = useStats();
  const [settings, setSettings] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user?.isAdmin) return;
    api.get('/lab-settings').then(r => setSettings(r.data)).catch(() => {});
  }, [user]);

  const dismiss = useCallback(async () => {
    setDismissing(true);
    try {
      const r = await api.post('/lab-settings/onboarding/dismiss');
      setSettings(r.data);
    } catch {
      setDismissing(false);
    }
  }, []);

  const steps = useMemo(() => {
    if (!settings) return [];
    return [
      {
        key: 'quantities',
        done: (settings.enabledQuantities || []).length > 0,
        path: '/lab-settings',
      },
      { key: 'lab-info', done: !!settings.labInfoComplete, path: '/lab-settings' },
      { key: 'etalon', done: (stats.etalons || 0) > 0, path: '/etalons' },
      { key: 'methodology', done: (stats.methodologies || 0) > 0, path: '/methodology' },
      { key: 'instrument', done: (stats.instruments || 0) > 0, path: '/instruments' },
    ];
  }, [settings, stats]);

  // Nezobrazovat: nenačteno, ne-admin, přeskočeno, nebo vše hotové.
  if (!user?.isAdmin || !settings || settings.onboardingDismissed) return null;
  if (steps.length > 0 && steps.every(s => s.done)) return null;

  const doneCount = steps.filter(s => s.done).length;

  return (
    <section
      className="rounded-lg border p-5"
      style={{
        background: 'var(--ct-surface)',
        borderColor: 'var(--ct-border)',
        boxShadow: 'var(--ct-shadow-sm)',
      }}
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="ct-h2">{t('onboarding.title')}</h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>
            {t('onboarding.subtitle', { done: doneCount, total: steps.length })}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          disabled={dismissing}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors"
          style={{ color: 'var(--ct-text-secondary)' }}
          title={t('onboarding.skip-all')}
        >
          <X className="w-3.5 h-3.5" />
          {t('onboarding.skip-all')}
        </button>
      </div>

      <ul className="mt-3 space-y-1">
        {steps.map((step) => (
          <li key={step.key}>
            <button
              type="button"
              onClick={() => navigate(step.path)}
              className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all duration-200"
              style={{
                background: step.done ? 'var(--ct-success-soft)' : 'var(--ct-surface)',
                borderColor: 'var(--ct-border)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ct-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ct-border)'; }}
            >
              {step.done ? (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--ct-success)' }} />
              ) : (
                <Circle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--ct-text-secondary)' }} />
              )}
              <div className="flex-1 min-w-0">
                <div
                  className="text-sm font-medium"
                  style={{
                    color: step.done ? 'var(--ct-success)' : 'var(--ct-text)',
                    textDecoration: step.done ? 'line-through' : 'none',
                  }}
                >
                  {t(`onboarding.steps.${step.key}.title`)}
                </div>
                {!step.done && (
                  <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>
                    {t(`onboarding.steps.${step.key}.description`)}
                  </div>
                )}
              </div>
              {!step.done && (
                <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" style={{ color: 'var(--ct-text-secondary)' }} />
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
